﻿import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import {
  setConnectionRequests,
  addConnectionRequest,
  removeConnectionRequest,
  setUnreadCounts,
  incrementUnreadCount,
  setUserOnline,
  setUserOffline,
  setOnlineStatus,
  bumpConnectionsVersion,
} from '../redux/chatSlice';
import { bumpScoreCheckVersion } from '../redux/scoreSlice';
import { useChatHub } from './useChatHub';
import { useSession } from '../SessionContext';
import { useApiClient } from '../ApiClientProvider';
import { ConnectionService } from '../services/connectionService';
import { ConversationService } from '../services/conversationService';
import { IConnectionRequest } from '../interfaces/IChatUser';
import { useNotifications } from '@toolpad/core';

interface UseChatSystemOptions {
  onMessageForUI?: (fromUserId: string, message: string) => void;
  onUserTyping?: (fromUserId: string) => void;
  onUserStoppedTyping?: (fromUserId: string) => void;
  onMessagesRead?: (conversationId: string, readerUserId: string) => void;
  onMessageReactionChanged?: (payload: { messageId: string; userId: string; action: string; reactionType: string; counts: Record<string, number> }) => void;
  onRequestProcessing?: (requestId: string, processing: boolean) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

export const useChatSystem = ({ onMessageForUI, onUserTyping, onUserStoppedTyping, onMessagesRead, onMessageReactionChanged, onRequestProcessing, onConnected, onDisconnected }: UseChatSystemOptions = {}) => {
  const { session } = useSession();
  const apiClient = useApiClient('applicationClient');

  // ✅ Memoize connectionService to prevent recreation on every render
  const connectionService = useMemo(() => new ConnectionService(apiClient), [apiClient]);
  const conversationService = useMemo(() => new ConversationService(apiClient), [apiClient]);

  const dispatch = useDispatch();
  const notifications = useNotifications();

  const connectionRequests = useSelector((state: RootState) => state.chat.connectionRequests);
  const unreadCounts = useSelector((state: RootState) => state.chat.unreadCounts);

  useEffect(() => {
    async function fetchPendingRequests() {
      if (!session) return;
      try {
        const pendingRes = await connectionService.getAll({ filters: { Status: "Pending" } });
        const requests: IConnectionRequest[] = (pendingRes.items as any[]).map((raw: any) => ({
          id: raw.otherUserId,
          from: {
            id: raw.otherUserId,
            name: raw.otherUserName ?? "Unknown",
            avatar: raw.otherUserImage ?? "",
            isOnline: raw.isOnline ?? false,
            lastSeen: raw.lastSeen ?? "",
          },
          timestamp: raw.requestedAt,
          message: raw.message ?? "",
          isRequestedByMe: raw.isRequestedByMe ?? false,
        }));
        dispatch(setConnectionRequests(requests));
      } catch (error) {
        console.error('Failed to fetch pending requests:', error);
      }
    }
    fetchPendingRequests();
  }, [session, dispatch, connectionService]);

  useEffect(() => {
    async function hydrateUnreadCounts() {
      if (!session?.user?.id) {
        dispatch(setUnreadCounts({}));
        return;
      }

      try {
        // Use the conversation list endpoint which now provides server-calculated unread counts.
        // The server caps pageSize at 50, so paginate to cover users with many conversations.
        const pageSize = 50;
        let page = 1;
        let hasMore = true;
        const nextUnreadCounts: { [userId: string]: number } = {};

        while (hasMore) {
          const conversationsRes = await conversationService.getRecentConversations(page, pageSize);
          const conversations = conversationsRes.items ?? [];

          // Use the server-provided unread count for each conversation
          conversations.forEach((conversation: any) => {
            const otherUserId = conversation.otherUserId as string | undefined;
            const unreadCount = conversation.unreadCount as number | undefined;

            if (otherUserId && unreadCount !== undefined) {
              nextUnreadCounts[otherUserId] = unreadCount;
            }
          });

          hasMore = conversations.length === pageSize;
          page++;

          // Safety check: don't fetch more than 10 pages (500 conversations)
          if (page > 10) {
            break;
          }
        }

        dispatch(setUnreadCounts(nextUnreadCounts));
      } catch (error) {
        console.error('Failed to hydrate unread counts:', error);
      }
    }

    hydrateUnreadCounts();
  }, [session?.user?.id, dispatch, conversationService]);

  useChatHub({
    token: session?.token || '',
    onMessageReceived: (fromUserId: string, _message: string, _conversationId: string | null, _messageId: string | null, _createdAtIso?: string) => {
      dispatch(incrementUnreadCount(fromUserId));
      // Pass conversationId and messageId to UI handler if needed
      onMessageForUI?.(fromUserId, _message);
      // The sender may have just earned the "they reply within 7 days" bonus — re-check
      // for a celebration immediately (network-score.md section 3).
      dispatch(bumpScoreCheckVersion());
    },
    onNewConnectionRequest: (request: IConnectionRequest) => {
      dispatch(addConnectionRequest(request));
      notifications.show(`${request.from.name} sent you a connection request`, {
        severity: 'info',
        autoHideDuration: 5000,
      });
    },
    onUserTyping: (userId: string, conversationId?: string | null) => {
      onUserTyping?.(userId);
    },
    onUserStoppedTyping: (userId: string, conversationId?: string | null) => {
      onUserStoppedTyping?.(userId);
    },
    onMessagesRead: (conversationId: string, readerUserId: string, lastReadMessageId?: string) => {
      onMessagesRead?.(conversationId, readerUserId);
    },
    onMessageReactionChanged: (payload: { messageId: string; userId: string; action: string; reactionType: string; counts: Record<string, number> }) => {
      onMessageReactionChanged?.(payload);
    },
    onUserOnline: (userId: string) => {
      dispatch(setUserOnline(userId));
    },
    onUserOffline: (userId: string) => {
      dispatch(setUserOffline(userId));
    },
    onConnectionAccepted: (userId: string) => {
      dispatch(removeConnectionRequest(userId));
      dispatch(bumpConnectionsVersion());
    },
    onConnectionRejected: (userId: string) => {
      dispatch(removeConnectionRequest(userId));
    },
    onConnectionCanceled: (userId: string) => {
      dispatch(removeConnectionRequest(userId));
    },
    onConnectionRemoved: (userId: string) => {
      // Remove any chat with this user as a participant
      const state: RootState = (window as any).store?.getState?.() || {};
      const chats = state.chat?.chats || [];
      chats.forEach((chat: any) => {
        if (chat.participants.some((p: any) => p.id === userId)) {
          dispatch({ type: 'chat/removeChat', payload: chat.id });
        }
      });
      dispatch(bumpConnectionsVersion());
    },
    onConnected: () => {
      onConnected?.();
      (async () => {
        const ids = await window.chatHub?.getOnlineUsers?.();
        if (ids && Array.isArray(ids)) {
          const presence: { [id: string]: boolean } = {};
          ids.forEach((id) => (presence[id] = true));
          dispatch(setOnlineStatus(presence));
        }
      })();
    },
    onDisconnected: () => {
      onDisconnected?.();
    },
  });

  const handleAcceptRequest = useCallback(async (requestId: string) => {
    const request = connectionRequests.find((req: IConnectionRequest) => req.id === requestId);
    if (!request) {
      return;
    }

    onRequestProcessing?.(requestId, true);
    try {
      await connectionService.accept(request.from.id);
      dispatch(removeConnectionRequest(requestId));
      dispatch(bumpConnectionsVersion());
      notifications.show(`You are now connected with ${request.from.name}`, {
        severity: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Failed to accept connection:', error);
      notifications.show(`Failed to accept connection: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      onRequestProcessing?.(requestId, false);
    }
  }, [connectionRequests, connectionService, dispatch, notifications, onRequestProcessing]);

  const handleRejectRequest = useCallback(async (requestId: string) => {
    const request = connectionRequests.find((req: IConnectionRequest) => req.id === requestId);
    if (!request) {
      return;
    }

    onRequestProcessing?.(requestId, true);
    try {
      await connectionService.reject(request.from.id);
      dispatch(removeConnectionRequest(requestId));
      notifications.show(`Declined connection request from ${request.from.name}`, {
        severity: 'info',
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('Failed to reject connection:', error);
      notifications.show(`Failed to decline connection: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      onRequestProcessing?.(requestId, false);
    }
  }, [connectionRequests, connectionService, dispatch, notifications, onRequestProcessing]);

  const handleCancelRequest = useCallback(async (requestId: string) => {
    const request = connectionRequests.find((req: IConnectionRequest) => req.id === requestId);
    console.log('🚫 Cancel request called. RequestId:', requestId, 'Found request:', request);
    if (!request) {
      console.warn('⚠️ Request not found in Redux state. Current requests:', connectionRequests);
      return;
    }

    onRequestProcessing?.(requestId, true);
    try {
      console.log('🔄 Canceling connection request to:', request.from.name, 'userId:', request.from.id);
      await connectionService.remove(request.from.id);
      console.log('✅ Cancel API call succeeded');
      dispatch(removeConnectionRequest(requestId));
      notifications.show(`Canceled connection request to ${request.from.name}`, {
        severity: 'info',
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('❌ Failed to cancel connection:', error);
      notifications.show(`Failed to cancel request: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      onRequestProcessing?.(requestId, false);
    }
  }, [connectionRequests, connectionService, dispatch, notifications, onRequestProcessing]);

  const handleSendRequest = useCallback(async (otherUserId: string, eventId: string = "") => {
    try {
      console.log('📤 Sending connection request to userId:', otherUserId);
      const createdConnection = await connectionService.create(otherUserId, eventId);
      console.log('✅ Connection request sent successfully:', createdConnection);

      // Add to Redux state as an outgoing request immediately
      const outgoingRequest: IConnectionRequest = {
        id: otherUserId, // Use otherUserId for consistency
        from: {
          id: otherUserId,
          name: (createdConnection as any).otherUserName ?? "Unknown",
          avatar: (createdConnection as any).otherUserImage ?? "",
          isOnline: false,
          lastSeen: "",
        },
        timestamp: new Date().toISOString(),
        message: "",
        isRequestedByMe: true, // Mark as outgoing
      };

      dispatch(addConnectionRequest(outgoingRequest));

      notifications.show('Connection request sent', {
        severity: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      console.error('❌ Failed to send connection request:', error);
      notifications.show(`Failed to send request: ${error instanceof Error ? error.message : 'Unknown error'}`, {
        severity: 'error',
        autoHideDuration: 3000,
      });
      throw error; // Re-throw so caller can handle
    }
  }, [connectionService, dispatch, notifications]);

  const sendTypingToUser = async (userId: string) => {
    if (typeof window !== 'undefined' && window.chatHub) {
      await window.chatHub.sendTyping!(userId);
    }
  };

  const sendStopTypingToUser = async (userId: string) => {
    if (typeof window !== 'undefined' && window.chatHub) {
      await window.chatHub.sendStopTyping!(userId);
    }
  };

  return {
    connectionRequests,
    unreadCounts,
    handleAcceptRequest,
    handleRejectRequest,
    handleCancelRequest,
    handleSendRequest,
    sendTypingToUser,
    sendStopTypingToUser,
  };
};